/**
 * GH-268 — Onboarding tour step config.
 *
 * Eight-step coach-mark tour anchored to real DOM elements via CSS selectors.
 * Phase 4 owns adding the matching `data-tour="..."` attributes to existing
 * surfaces; this file is the source of truth for the step list and i18n keys.
 */

export type TourStepId =
  | 'home'
  | 'packages'
  | 'pkgHome'
  | 'trust'
  | 'complete'
  | 'upload'
  | 'storage'
  | 'graph'

export interface TourStep {
  /** Stable id used in tests + analytics */
  id: TourStepId
  /**
   * Route to navigate to before showing the step.
   * `:templateId` placeholder is resolved at runtime against the first
   * `is_template=true` onboarding package.
   */
  route: string
  /** CSS selector pinned to the anchor element (added by Phase 4) */
  selector: string
  /**
   * i18n key under the `tour` namespace. Each step expects sub-keys
   * `.title` and `.body` (Phase 5 ships the strings).
   */
  i18nKey: string
}

export const TEMPLATE_ID_PLACEHOLDER = ':templateId'

export const TOUR_STEPS: readonly TourStep[] = [
  { id: 'home',     route: '/',                                        selector: '[data-tour="home-dashboard"]',  i18nKey: 'tour.steps.home' },
  { id: 'packages', route: '/packages',                                selector: '[data-tour="package-list"]',    i18nKey: 'tour.steps.packages' },
  { id: 'pkgHome',  route: `/packages/${TEMPLATE_ID_PLACEHOLDER}`,    selector: '[data-tour="package-home"]',    i18nKey: 'tour.steps.pkgHome' },
  { id: 'trust',    route: `/packages/${TEMPLATE_ID_PLACEHOLDER}`,    selector: '[data-tour="trust-panel"]',     i18nKey: 'tour.steps.trust' },
  { id: 'complete', route: `/packages/${TEMPLATE_ID_PLACEHOLDER}`,    selector: '[data-tour="completeness"]',    i18nKey: 'tour.steps.completeness' },
  { id: 'upload',   route: '/ingest/upload',                           selector: '[data-tour="upload-context"]',  i18nKey: 'tour.steps.upload' },
  { id: 'storage',  route: '/storage',                                 selector: '[data-tour="storage-grouped"]', i18nKey: 'tour.steps.storage' },
  { id: 'graph',    route: '/graph',                                   selector: '[data-tour="graph-canvas"]',    i18nKey: 'tour.steps.graph' },
] as const

/** Resolve `:templateId` in a route against the seeded template package id. */
export function resolveStepRoute(route: string, templateId: string | null): string {
  if (!route.includes(TEMPLATE_ID_PLACEHOLDER)) return route
  if (!templateId) return route // caller must guard
  return route.replace(TEMPLATE_ID_PLACEHOLDER, templateId)
}
